import { useRef, useState } from "react";
import WriteDivField from "../Components/WriteDivField";
import { TabView, TabPanel } from "primereact/tabview";
import { normalizeNumber } from "../utils";
import { DeleteSweetAlert } from "../DeleteSweetAlert";

let rowIdCounter = 0;
const getUniqueRowId = () => ++rowIdCounter;

const getInitialRow = () => ({
  id: getUniqueRowId(),
  item: "",
  pcs: "",
  qty: "",
  rate: "",
  amount: "",
  discount: "",
  discountAmt: "",
  finalAmt: "",
});

const getInitialRows = () => [
  getInitialRow(),
  getInitialRow(),
  getInitialRow(),
];

export default function Retail() {
  // Tabs state
  const [tabs, setTabs] = useState([
    {
      name: "Tab 1",
      rows: getInitialRows(),
    },
  ]);
  const [activeIndex, setActiveIndex] = useState(0);

  // Refs for WriteDivFields: rows x fields
  const rowRefs = useRef({});

  // Get current tab's and rows
  const rows = tabs[activeIndex]?.rows || getInitialRows();

  // Helper for numerals only
  const numeralsOnly = (input) => normalizeNumber(input).replace(/[^0-9]/g, "");

  // Update rows for current tab
  const setRowsForTab = (newRows) => {
    setTabs((prevTabs) => {
      const updated = [...prevTabs];
      updated[activeIndex] = {
        ...updated[activeIndex],
        rows: newRows,
      };
      return updated;
    });
  };

  // Delete row by index and clear WriteDivField DOM content
  const deleteRow = (rowIndex) => {
    const rowId = rows[rowIndex].id;
    if (rowRefs.current[rowId]) {
      rowRefs.current[rowId].forEach((ref) => {
        if (ref && ref.innerText !== undefined) ref.innerText = "";
      });
      delete rowRefs.current[rowId];
    }
    const newRows = rows.filter((_, i) => i !== rowIndex);
    setRowsForTab(newRows);
  };

  const handeRemoveRowConfirm = async (data, index) => {
    if (data && index !== undefined && index !== null) {
      const response = () => deleteRow(index);
      DeleteSweetAlert({
        title: "Are you sure you want to remove this entry?",
        icon1: "warning",
        title2: `${data?.item}`,
        callApi: response,
        callSuccessModal: false,
      });
    }
  };

  // Handle input for any field in a row
  const handleInput = (rowIndex, field, value) => {
    const newRows = [...rows];
    if (field === "qty" || field === "amount") {
      const normalized = normalizeNumber(value);
      newRows[rowIndex][field] = normalized.replace(/[^0-9]/g, "");
    } else if (field === "pcs") {
      newRows[rowIndex][field] = value;
    } else {
      newRows[rowIndex][field] = value;
    }

    if (field === "qty" || field === "rate") {
      const qty = parseInt(
        field === "qty"
          ? normalizeNumber(value).replace(/[^0-9]/g, "")
          : newRows[rowIndex].qty || "0",
        10
      );
      const rate = parseInt(
        field === "rate"
          ? normalizeNumber(value).replace(/[^0-9]/g, "")
          : newRows[rowIndex].rate || "0",
        10
      );
      if (!isNaN(qty) && !isNaN(rate)) {
        newRows[rowIndex].amount = (qty * rate).toString();
      } else {
        newRows[rowIndex].amount = "";
      }
    }

    const amount = parseInt(newRows[rowIndex].amount || "0", 10);
    const discount = parseInt(newRows[rowIndex].discount || "0", 10);
    if (!isNaN(amount) && !isNaN(discount)) {
      const discountAmt = Math.round((amount * discount) / 100);
      const finalAmt = amount - discountAmt;
      newRows[rowIndex].discountAmt = amount ? discountAmt.toString() : "";
      newRows[rowIndex].finalAmt = amount ? finalAmt.toString() : "";
    } else {
      newRows[rowIndex].discountAmt = "";
      newRows[rowIndex].finalAmt = "";
    }

    if (rowIndex === newRows.length - 2) {
      for (let i = 0; i < 5; i++) {
        newRows.push(getInitialRow());
      }
    }

    setRowsForTab(newRows);
  };

  // Add new tab (max 10)
  const addTab = () => {
    if (tabs.length >= 10) return;
    setTabs((prevTabs) => [
      ...prevTabs,
      {
        name: `Tab ${prevTabs.length + 1}`,
        rows: getInitialRows(),
      },
    ]);
    setActiveIndex(tabs.length);
    rowRefs.current = [];
  };

  // Remove tab
  const removeTab = (i) => {
    if (tabs.length === 1) return;
    const newTabs = tabs.filter((_, idx) => idx !== i);
    setTabs(newTabs);
    setActiveIndex((prev) =>
      prev > i ? prev - 1 : Math.max(0, prev - (prev === i ? 1 : 0))
    );
    rowRefs.current = [];
  };

  return (
    <div className="m-[0.3125rem_0.375rem]">
      <TabView
        activeIndex={activeIndex}
        onTabChange={(e) => setActiveIndex(e.index)}
        renderActiveOnly={true}
      >
        {tabs?.map((tab, i) => (
          <TabPanel
            key={i}
            header={
              <span
                className={`flex items-center gap-2 ${
                  i === activeIndex && i !== tabs?.length - 1
                    ? "py-1.5"
                    : i === tabs?.length - 1
                    ? "relative py-1"
                    : "py-1.5"
                }`}
              >
                {tab?.name}
                {/* {tabs?.length > 1 && (
                  <i
                    className="pi pi-times text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeTab(i);
                    }}
                  ></i>
                )} */}
                {i === tabs?.length - 1 && (
                  <div
                    className="p-1 flex items-center justify-center rounded-sm"
                    style={{ backgroundColor: "#6366f1" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      addTab();
                    }}
                  >
                    <i className="pi pi-plus text-xs text-white"></i>
                  </div>
                )}
              </span>
            }
          >
            <div className="mt-2">
              {rows.map((row, index) => {
                const rowId = row.id;
                if (!rowRefs.current[rowId]) rowRefs.current[rowId] = [];
                return (
                  <div
                    key={rowId}
                    className="flex select-none w-full"
                    tabIndex={0}
                    onDoubleClick={() => handeRemoveRowConfirm(row, index)}
                  >
                    <WriteDivField
                      ref={(el) => {
                        if (!rowRefs.current[rowId])
                          rowRefs.current[rowId] = [];
                        if (el) {
                          rowRefs.current[rowId][0] = el;
                        } else {
                          rowRefs.current[rowId][0] = undefined;
                        }
                      }}
                      style={{ border: "1px solid black" }}
                      parentClassName={`w-[32%] h-[3rem] outline-none border border-black p-0.5`}
                      className="w-full h-full outline-none text-[1.5rem] flex items-center"
                      handleInput={(val) => handleInput(index, "item", val)}
                      placeholder="Item"
                      value={row.item}
                    />
                    <WriteDivField
                      ref={(el) => {
                        if (!rowRefs.current[rowId])
                          rowRefs.current[rowId] = [];
                        if (el) {
                          rowRefs.current[rowId][1] = el;
                        } else {
                          rowRefs.current[rowId][1] = undefined;
                        }
                      }}
                      style={{ border: "1px solid black" }}
                      parentClassName={`w-[12%] h-[3rem] outline-none border border-black p-0.5`}
                      className="w-full h-full outline-none text-[1.5rem] flex items-center"
                      handleInput={(val) => handleInput(index, "pcs", val)}
                      placeholder="Pices"
                      value={row.pcs}
                    />
                    <WriteDivField
                      ref={(el) => {
                        if (!rowRefs.current[rowId])
                          rowRefs.current[rowId] = [];
                        if (el) {
                          rowRefs.current[rowId][2] = el;
                        } else {
                          rowRefs.current[rowId][2] = undefined;
                        }
                      }}
                      style={{ border: "1px solid black" }}
                      parentClassName={`w-[10%] h-[3rem] outline-none border border-black p-0.5`}
                      className="w-11/12 h-full outline-none text-[1.5rem] flex items-center"
                      handleInput={(val) => handleInput(index, "qty", val)}
                      placeholder="Qty"
                      value={row.qty}
                      sanitize={numeralsOnly}
                    />
                    <WriteDivField
                      ref={(el) => {
                        if (!rowRefs.current[rowId])
                          rowRefs.current[rowId] = [];
                        if (el) {
                          rowRefs.current[rowId][3] = el;
                        } else {
                          rowRefs.current[rowId][3] = undefined;
                        }
                      }}
                      style={{ border: "1px solid black" }}
                      parentClassName={`w-[11%] h-[3rem] outline-none border border-black p-0.5`}
                      className="w-11/12 h-full outline-none text-[1.5rem] flex items-center"
                      handleInput={(val) => handleInput(index, "rate", val)}
                      placeholder="Rate"
                      value={row.rate}
                      sanitize={numeralsOnly}
                    />
                    <WriteDivField
                      ref={(el) => {
                        if (!rowRefs.current[rowId])
                          rowRefs.current[rowId] = [];
                        if (el) {
                          rowRefs.current[rowId][4] = el;
                        } else {
                          rowRefs.current[rowId][4] = undefined;
                        }
                      }}
                      style={{ border: "1px solid black" }}
                      parentClassName="w-[11%] h-[3rem] outline-none border border-black p-0.5"
                      className="w-11/12 h-full outline-none text-[1.5rem] flex items-center"
                      handleInput={(val) => handleInput(index, "discount", val)}
                      placeholder="Disc%"
                      value={row.discount}
                      sanitize={numeralsOnly}
                    />
                    <WriteDivField
                      ref={(el) => {
                        if (!rowRefs.current[rowId])
                          rowRefs.current[rowId] = [];
                        if (el) {
                          rowRefs.current[rowId][5] = el;
                        } else {
                          rowRefs.current[rowId][5] = undefined;
                        }
                      }}
                      style={{ border: "1px solid black" }}
                      parentClassName="w-[12%] h-[3rem] outline-none border border-black p-0.5 bg-gray-100"
                      className="w-11/12 h-full outline-none text-[1.5rem] flex items-center"
                      handleInput={() => {}}
                      placeholder="Disc Amt"
                      value={row.discountAmt}
                      showClearIcon={false}
                    />
                    <WriteDivField
                      ref={(el) => {
                        if (!rowRefs.current[rowId])
                          rowRefs.current[rowId] = [];
                        if (el) {
                          rowRefs.current[rowId][6] = el;
                        } else {
                          rowRefs.current[rowId][6] = undefined;
                        }
                      }}
                      style={{ border: "1px solid black" }}
                      parentClassName="w-[12%] h-[3rem] outline-none border border-black p-0.5 bg-gray-100"
                      className="w-11/12 h-full outline-none text-[1.5rem] flex items-center"
                      handleInput={() => {}}
                      placeholder="Final"
                      value={row.finalAmt}
                      showClearIcon={false}
                    />
                  </div>
                );
              })}
              <div className="flex justify-end mt-2 text-xl font-bold">
                {`Total: ${rows
                  .map((r) => parseInt(r.finalAmt || "0", 10))
                  .reduce((a, b) => a + b, 0)}`}
              </div>
            </div>
          </TabPanel>
        ))}
      </TabView>
    </div>
  );
}
